/**
 * Research-Based VRP Schedule View - Minimal Layout
 * No headers - Pure data focus following academic patterns
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { RouteGanttChart } from '../components/scheduling/RouteGanttChart';
import { useVehicles } from '../hooks/useVehicles';
import apiClient from '../services/api';
import { generateMockOptimizationRuns, generateMockRouteSteps } from '../components/scheduling/mockRouteData';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';

export const ScheduleView: React.FC = () => {
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  const { data: vehicles = [] } = useVehicles(currentProject?.id || 0);
  
  // State for optimization runs and route data
  const [optimizationRuns, setOptimizationRuns] = useState<any[]>([]);
  const [selectedOptimizationRun, setSelectedOptimizationRun] = useState<number | undefined>();
  const [routeSteps, setRouteSteps] = useState<any[]>([]);
  const [ganttData, setGanttData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setUsingMockData] = useState(false);
  const [chartDateRange, setChartDateRange] = useState<{startDate: Date, endDate: Date}>({
    startDate: new Date(new Date().setHours(6, 0, 0, 0)),
    endDate: new Date(new Date().setHours(22, 0, 0, 0))
  });

  // Fetch optimization runs from real API - prioritize real data
  const fetchOptimizationRuns = useCallback(async () => {
    if (!currentProject?.id) return;
    
    try {
      setLoading(true);
      console.log('🔍 Fetching optimization runs for project:', currentProject.id);
      const runs = await apiClient.getOptimizationRuns(currentProject.id, { limit: 10 });
      
      if (runs && runs.length > 0) {
        console.log('✅ Found real optimization runs:', runs.length);
        setOptimizationRuns(runs);
        setUsingMockData(false);
        
        // Auto-select latest run if none selected
        if (!selectedOptimizationRun) {
          setSelectedOptimizationRun(runs[0].id);
        }
      } else {
        console.log('⚠️ No optimization runs found - schedule view will show empty state');
        setOptimizationRuns([]);
        setUsingMockData(false);
        setSelectedOptimizationRun(undefined);
      }
    } catch (error) {
      console.error('❌ Error fetching optimization runs:', error);
      setOptimizationRuns([]);
      setUsingMockData(false);
      setSelectedOptimizationRun(undefined);
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, selectedOptimizationRun]);

  // Fetch route steps for selected optimization run - real data only
  const fetchRouteSteps = useCallback(async () => {
    if (!currentProject?.id || !selectedOptimizationRun) {
      setRouteSteps([]);
      setGanttData([]);
      return;
    }
    
    try {
      console.log('🔍 Fetching route steps for optimization run:', selectedOptimizationRun);
      
      const steps = await apiClient.getRouteSteps(currentProject.id);
      console.log('📊 All route steps fetched:', steps?.length || 0);
      
      // Filter steps for the selected optimization run
      const filteredSteps = steps.filter(step => {
        // Check both optimization_run_id and nested route_summaries relationship
        return step.optimization_run_id === selectedOptimizationRun ||
               step.route_summaries?.optimization_run_id === selectedOptimizationRun;
      });
      
      console.log('🎯 Filtered route steps for run', selectedOptimizationRun, ':', filteredSteps?.length || 0);
      
      if (filteredSteps.length > 0) {
        console.log('✅ Using real route steps data');
        setRouteSteps(filteredSteps);
        transformRouteStepsToGantt(filteredSteps);
        setUsingMockData(false);
      } else {
        console.log('⚠️ No route steps found for selected optimization run');
        setRouteSteps([]);
        setGanttData([]);
        setUsingMockData(false);
      }
    } catch (error) {
      console.error('❌ Error fetching route steps:', error);
      setRouteSteps([]);
      setGanttData([]);
      setUsingMockData(false);
    }
  }, [currentProject?.id, selectedOptimizationRun]);

  // Transform route_steps data into research-based format
  const transformRouteStepsToGantt = useCallback((steps: any[]) => {
    if (!steps.length) {
      setGanttData([]);
      return;
    }

    console.log('🔄 [SCHEDULE TRANSFORM] Starting transformation of', steps.length, 'steps');

    // Group steps by vehicle for research-based visualization
    const routesByVehicle = steps.reduce((acc: any, step: any) => {
      // CRITICAL FIX: vehicle_id comes from route_summaries relationship
      const vehicleId = step.route_summaries?.vehicle_id || step.vehicle_id;
      if (!vehicleId) {
        console.warn('⚠️ Step missing vehicle_id:', step);
        return acc;
      }
      if (!acc[vehicleId]) {
        acc[vehicleId] = [];
      }
      acc[vehicleId].push(step);
      return acc;
    }, {});

    console.log('🔄 [SCHEDULE TRANSFORM] Grouped by vehicle:', Object.keys(routesByVehicle).length, 'vehicles');

    const ganttRows = Object.entries(routesByVehicle).map(([vehicleId, vehicleSteps]: [string, any]) => {
      const sortedSteps = vehicleSteps.sort((a: any, b: any) => a.step_order - b.step_order);
      
      // Enhanced vehicle metadata following SAP pattern
      const vehicle = vehicles.find(v => v.id === parseInt(vehicleId)) || {
        id: parseInt(vehicleId),
        description: `Vehicle ${vehicleId}`,
        profile: 'driving',
        capacity: [0],
        type: 'standard'
      };

      // Create activity segments following VeRoViz patterns
      const segments: any[] = [];
      const baseDate = new Date();
      baseDate.setHours(0, 0, 0, 0); // Midnight base - VROOM times are absolute seconds since midnight

      sortedSteps.forEach((step: any, index: number) => {
        // VROOM arrival_time is already absolute seconds since midnight, no need for additional offset
        const arrivalTime = new Date(baseDate.getTime() + (step.arrival_time || 0) * 1000);
        
        // Travel segments between locations
        if (index > 0) {
          const previousStep = sortedSteps[index - 1];
          const travelStartTime = new Date(baseDate.getTime() + ((previousStep.arrival_time || 0) + (previousStep.service_time || 0) + (previousStep.setup_time || 0)) * 1000);
          const travelDuration = (step.arrival_time || 0) - ((previousStep.arrival_time || 0) + (previousStep.service_time || 0) + (previousStep.setup_time || 0));
          
          if (travelDuration > 0) {
            segments.push({
              id: `travel-${vehicleId}-${index}`,
              vehicleId: parseInt(vehicleId),
              startTime: travelStartTime,
              endTime: arrivalTime,
              type: 'travel', // Standard research color: orange
              status: 'scheduled',
              duration: Math.max(0, travelDuration / 60),
              location: `${step.location_name || `Location ${step.location_id}`}`,
              distance: step.distance
            });
          }
        }
        
        // Depot start/end activities
        if (step.step_type === 'start') {
          segments.push({
            id: `depot-start-${vehicleId}-${index}`,
            vehicleId: parseInt(vehicleId),
            startTime: arrivalTime,
            endTime: arrivalTime,
            type: 'depot_start', // Research color: blue
            status: 'completed',
            duration: 0,
            location: 'Depot Start'
          });
        } else if (step.step_type === 'end') {
          segments.push({
            id: `return-${vehicleId}-${index}`,
            vehicleId: parseInt(vehicleId),
            startTime: arrivalTime,
            endTime: arrivalTime,
            type: 'return', // Research color: purple
            status: 'scheduled',
            duration: 0,
            location: 'Return to Depot'
          });
        } else if (step.step_type === 'job') {
          let currentTime = arrivalTime;
          
          // Setup activities
          if ((step.setup_time || 0) > 0) {
            const setupEnd = new Date(currentTime.getTime() + (step.setup_time || 0) * 1000);
            segments.push({
              id: `setup-${vehicleId}-${step.job_id}-${index}`,
              vehicleId: parseInt(vehicleId),
              jobId: step.job_id,
              startTime: currentTime,
              endTime: setupEnd,
              type: 'setup',
              status: 'scheduled',
              duration: Math.max(0, (step.setup_time || 0) / 60),
              location: `Setup at ${step.location_name || `Job ${step.job_id}`}`
            });
            currentTime = setupEnd;
          }

          // Service activities (core customer service)
          if ((step.service_time || 0) > 0) {
            const serviceEnd = new Date(currentTime.getTime() + (step.service_time || 0) * 1000);
            segments.push({
              id: `service-${vehicleId}-${step.job_id}-${index}`,
              vehicleId: parseInt(vehicleId),
              jobId: step.job_id,
              startTime: currentTime,
              endTime: serviceEnd,
              type: 'service', // Research color: green
              status: 'scheduled',
              duration: Math.max(0, (step.service_time || 0) / 60),
              location: step.location_name || `Customer ${step.job_id}`
            });
          }
        }
      });

      // Enhanced metrics calculation with robust error handling
      const totalDuration = segments.reduce((sum, seg) => sum + (seg.duration || 0), 0);
      const totalDistance = sortedSteps.reduce((sum: number, step: any) => sum + (step.distance || 0), 0) / 1000;
      
      // Calculate utilization with proper error handling
      const rawUtilization = totalDuration > 0 ? (totalDuration / (12 * 60)) * 100 : 0;
      const utilization = Math.min(
        typeof rawUtilization === 'number' && !isNaN(rawUtilization) ? rawUtilization : 0, 
        100
      );
      
      const efficiency = vehicle.speed_factor || 1.0;

      const ganttRow = {
        vehicle: {
          id: vehicle.id,
          description: vehicle.description,
          type: vehicle.profile,
          capacity: vehicle.capacity || [0],
          efficiency: efficiency,
          profile: vehicle.profile
        },
        segments,
        utilization: Math.round(utilization),
        totalDistance: Math.round(totalDistance),
        totalDuration: Math.round(totalDuration),
        status: utilization > 90 ? 'delayed' : 'on-track' as const,
      };
      
      return ganttRow;
    });

    setGanttData(ganttRows);
    
    // CRITICAL FIX: Calculate the actual date range from segment data
    const allSegments = ganttRows.flatMap(row => row.segments);
    if (allSegments.length > 0) {
      const earliestTime = new Date(Math.min(...allSegments.map(seg => new Date(seg.startTime).getTime())));
      const latestTime = new Date(Math.max(...allSegments.map(seg => new Date(seg.endTime).getTime())));
      
      // Add padding: start 1 hour earlier, end 1 hour later
      const chartStart = new Date(earliestTime.getTime() - 60 * 60 * 1000);
      const chartEnd = new Date(latestTime.getTime() + 60 * 60 * 1000);
      
      console.log('📅 [SCHEDULE TRANSFORM] Calculated date range:');
      console.log('  Earliest segment:', earliestTime);
      console.log('  Latest segment:', latestTime);
      console.log('  Chart range:', chartStart, 'to', chartEnd);
      
      setChartDateRange({
        startDate: chartStart,
        endDate: chartEnd
      });
    }
  }, [vehicles]);

  // Load optimization runs when project changes
  useEffect(() => {
    if (currentProject?.id) {
      fetchOptimizationRuns();
    }
  }, [currentProject?.id, fetchOptimizationRuns]);

  // Load route steps when optimization run changes
  useEffect(() => {
    fetchRouteSteps();
  }, [fetchRouteSteps]);

  // Minimal layout - Full horizontal space utilization
  return (
    <ProfessionalLayout pageTitle="Route Schedule" pageSubtitle="Vehicle routing timeline and schedule management">
      <Box sx={{ 
        height: 'calc(100vh - 140px)', // Account for ProfessionalLayout header
        width: '100%',
        backgroundColor: '#ffffff', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden' // Prevent any scrolling on the main container
      }}>
      {!currentProject ? (
        <Box sx={{ 
          flex: 1,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2
        }}>
          <Typography variant="body1" color="#6B7280" sx={{ fontSize: '0.875rem' }}>
            No Project Selected
          </Typography>
        </Box>
      ) : !selectedOptimizationRun || ganttData.length === 0 ? (
        <Box sx={{ 
          flex: 1,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2
        }}>
          <Typography variant="body1" color="#6B7280" sx={{ fontSize: '0.875rem' }}>
            {!selectedOptimizationRun ? 'Select Optimization Run' : 'No Route Data Available'}
          </Typography>
        </Box>
      ) : (
        // Enhanced Gantt implementation with real data and advanced features - Full width
        <Box sx={{ 
          flex: 1,
          width: '100%',
          overflow: 'hidden' // Let the Gantt handle its own scrolling
        }}>
          <RouteGanttChart
            ganttData={ganttData}
            routeSteps={routeSteps}
            optimizationRuns={optimizationRuns}
            selectedOptimizationRun={selectedOptimizationRun}
            onOptimizationRunChange={setSelectedOptimizationRun}
            loading={loading}
            startDate={chartDateRange.startDate} // Dynamic range based on actual segment data
            endDate={chartDateRange.endDate} // Dynamic range based on actual segment data
            height="100%"
            enableVirtualScrolling={true}
            maxVisibleVehicles={50}
            enableMultiDay={true}
            enableFiltering={true}
            onSegmentClick={(segment) => {
              console.log('Enhanced segment interaction:', {
                type: segment.type,
                location: segment.location,
                duration: segment.duration,
                vehicle: segment.vehicleId,
                startTime: segment.startTime,
                endTime: segment.endTime,
                status: segment.status
              });
            }}
            onSegmentEdit={(segment) => console.log('Segment edit request:', segment)}
          />
        </Box>
      )}
      </Box>
    </ProfessionalLayout>
  );
};